import { useState, useEffect } from 'react'
import { useQuery } from "@apollo/client/react";
import { GET_PUBLIC_TEAM } from '@/housefolios-components/Team/queries'
import { GET_MARKETPLACES } from '@/housefolios-components/Marketplace/queries'

/**
 * Custom hook for public team page data
 */
export const usePublicTeam = (orgId) => {
  const { loading, error, data } = useQuery(GET_PUBLIC_TEAM, {
    variables: { orgId },
    skip: !orgId,
  })

  return {
    loading,
    error,
    teamMembers: data?.publicTeamMembers || [],
  }
}

/**
 * Custom hook for public marketplace data
 */
export const usePublicMarketplace = (
  marketplaceUrl,
  sold = false,
) => {
  const { loading, error, data } = useQuery(GET_MARKETPLACES, {
    variables: { marketplace: marketplaceUrl },
    skip: !marketplaceUrl,
  })

  const marketplaceData = data?.marketplaceProperties || {}
  const properties = marketplaceData.properties || []

  // Filter properties based on expiration date and sold status
  const filteredProperties = properties.filter((property) => {
    const isNotExpired =
      new Date(property?.marketplaces?.expirationDate).getTime() >
      Date.now()
    const isSold = property?.status?.toLowerCase() === 'sold'

    if (sold) {
      return isSold
    }
    return isNotExpired && !isSold
  })

  return {
    loading,
    error,
    properties: filteredProperties,
    marketplaceData,
    hasNextPage: marketplaceData.hasNextPage,
    cursor: marketplaceData.cursor,
  }
}

/**
 * Custom hook for URL parameters
 */
export const useUrlParams = () => {
  const [params, setParams] = useState({})

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const paramObj = {}

    for (const [key, value] of urlParams.entries()) {
      paramObj[key] = value
    }

    setParams(paramObj)
  }, [])

  return params
}

/**
 * Custom hook for subdomain handling
 */
export const useSubdomain = () => {
  const urlParams = useUrlParams()
  const subdomain = urlParams.subdomain || null
  const subdomainParam = subdomain ? `?subdomain=${subdomain}` : ''

  return {
    subdomain,
    subdomainParam,
  }
}

/**
 * Custom hook for form state management
 */
export const useFormState = (initialState = {}) => {
  const [formData, setFormData] = useState(initialState)
  const [errors, setErrors] = useState({})

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }))
    }
  }

  const setFieldError = (field, error) => {
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }))
  }

  const resetForm = () => {
    setFormData(initialState)
    setErrors({})
  }

  const isValid = Object.values(errors).every((error) => !error)

  return {
    formData,
    errors,
    updateField,
    setFieldError,
    resetForm,
    isValid,
  }
}
